import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const initialState = {
  errors: null,
  isLoading: false,
  businessProfile: null,
  userBusinesses: [],
  createBusinessSuccess: false,
};

const BusinessProfileSlice = createSlice({
  name: "businessProfile",
  initialState,
  reducers: {
    setErrors: (state, action) => {
      // ✅ FIX: Sanitize error to prevent React Error #31
      state.errors = typeof action.payload === 'string' ? action.payload : (action.payload?.message || "Operation failed");
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setBusinessProfile: (state, action) => {
      state.businessProfile = action.payload;
    },
    setUserBusinesses: (state, action) => {
      state.userBusinesses = action.payload;
    },
    setCreateBusinessSuccess: (state, action) => {
      state.createBusinessSuccess = action.payload;
    },
  },
});

/**
 * Create a new business profile
 * @param {Object} businessData - Business profile data
 */
export const createBusinessProfile = (businessData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setCreateBusinessSuccess(false));
  try {
    const URL = "business/profile";
    const response = await axiosInstance.post(URL, businessData);
    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setBusinessProfile(data));
      dispatch(setCreateBusinessSuccess(true));
      showNotification({
        message: message || "Business profile created successfully",
        theme: "light",
        type: "success",
      });
      // Refresh user's businesses list
      dispatch(listUserBusinesses());
      return { success: true, data, message };
    } else {
      const errorMsg = message || "Failed to create business profile";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to create business profile";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Get business profile by ID
 * @param {String} businessId - Business profile ID
 */
export const getBusinessProfile = (businessId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `business/profile/${businessId}`;
    const response = await axiosInstance.get(URL);
    const { success, data } = response?.data || {};

    if (success) {
      dispatch(setBusinessProfile(data));
      return { success: true, data };
    } else {
      dispatch(setBusinessProfile(null));
      return { success: false };
    }
  } catch (error) {
    const errorResponse = error?.response;
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorResponse?.data?.message || error.message || "Something went wrong.",
    }));
    dispatch(setBusinessProfile(null));
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Update business profile
 * @param {String} businessId - Business profile ID
 * @param {Object} updatedData - Updated business data
 */
export const updateBusinessProfile = (businessId, updatedData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `business/profile/${businessId}`;
    const response = await axiosInstance.patch(URL, updatedData);
    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setBusinessProfile(data));
      showNotification({
        message: message || "Business profile updated successfully",
        theme: "light",
        type: "success",
      });
      // Refresh user's businesses list
      dispatch(listUserBusinesses());
      return { success: true, data, message };
    } else {
      const errorMsg = message || "Failed to update business profile";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to update business profile";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Close business profile
 * @param {String} businessId - Business profile ID
 * @param {String} reason - Reason for closing
 */
export const closeBusinessProfile = (businessId, reason) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `business/profile/${businessId}/close`;
    const response = await axiosInstance.post(URL, { reason });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "Business profile closed successfully",
        theme: "light",
        type: "success",
      });
      // Refresh user's businesses list
      dispatch(listUserBusinesses());
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to close business profile";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to close business profile";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * List all businesses user is part of
 */
export const listUserBusinesses = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = "workspaces/me/business";
    const response = await axiosInstance.get(URL);
    const { success, data } = response?.data || {};

    if (success) {
      dispatch(setUserBusinesses(data || []));
      return { success: true, data };
    } else {
      dispatch(setUserBusinesses([]));
      return { success: false };
    }
  } catch (error) {
    const errorResponse = error?.response;
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorResponse?.data?.message || error.message || "Something went wrong.",
    }));
    dispatch(setUserBusinesses([]));
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Transfer ownership of business
 * @param {String} businessId - Business profile ID
 * @param {String} newOwnerId - New owner's user ID
 */
export const transferOwnership = (businessId, newOwnerId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const URL = `business/profile/${businessId}/transfer-ownership`;
    const response = await axiosInstance.post(URL, { newOwnerId });
    const { status, message } = response?.data || {};

    if (status === "success") {
      showNotification({
        message: message || "Ownership transferred successfully",
        theme: "light",
        type: "success",
      });
      // Refresh business profile and user's businesses list
      dispatch(getBusinessProfile(businessId));
      dispatch(listUserBusinesses());
      return { success: true, message };
    } else {
      const errorMsg = message || "Failed to transfer ownership";
      showNotification({
        message: errorMsg,
        theme: "light",
        type: "error",
      });
      return { success: false, message: errorMsg };
    }
  } catch (error) {
    const errorResponse = error?.response;
    const errorMsg =
      errorResponse?.data?.message ||
      error.message ||
      "Failed to transfer ownership";
    dispatch(setErrors({
      status: errorResponse?.status,
      statusText: errorResponse?.statusText,
      message: errorMsg,
    }));
    showNotification({
      message: errorMsg,
      theme: "light",
      type: "error",
    });
    return { success: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export const {
  setErrors,
  setLoading,
  setBusinessProfile,
  setUserBusinesses,
  setCreateBusinessSuccess,
} = BusinessProfileSlice.actions;

export default BusinessProfileSlice.reducer;
